import React from "react";
import { object, number } from 'yup';
import apiService from "../../../services/ApiService";
import TailwindSlider from "../../Tailwind/TailwindSlider";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";


export default function SensitivityFloatField({ name, item, field, currentSensitivity }) {
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache — do NOT invalidate SCENARIO_TREE_KEY (see useUpdateFieldValue.ts)
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };

    const sensitivityField = item.sensitivityModelData.fields[field.name];

    const originalValue = (field.currentValue.value) ? field.currentValue.value : 0;
    const initialValue = (sensitivityField) ? sensitivityField.value.value : originalValue
    const initialSliderValue = originalValue !== 0 ? Math.round(initialValue / originalValue * 10000) / 100 : 100;

    const [sliderValue, setSliderValue] = React.useState(initialSliderValue as number);
    const [fieldValue, setFieldValue] = React.useState(initialValue as number);
    const fieldValueRef = React.useRef(initialValue as number);
    const hasChangedRef = React.useRef(false);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();

    const sensitivityFieldRef = React.useRef(sensitivityField);
    sensitivityFieldRef.current = sensitivityField;

    React.useEffect(() => {
        const newOriginalValue = (field.currentValue.value) ? field.currentValue.value : 0;
        const newSensitivityField = item.sensitivityModelData.fields[field.name];
        const newValue = (newSensitivityField) ? newSensitivityField.value.value : newOriginalValue;
        const newSliderValue = newOriginalValue !== 0 ? Math.round(newValue / newOriginalValue * 10000) / 100 : 100;
        setFieldValue(newValue as number);
        setSliderValue(newSliderValue);
        fieldValueRef.current = newValue as number;
        hasChangedRef.current = false;
    }, [item.id, field.name, field.currentValue?.value]);

    // Save unsaved changes when the component unmounts (e.g. selecting another model_data)
    React.useEffect(() => {
        return () => {
            if (hasChangedRef.current && sensitivityFieldRef.current) {
                const val = fieldValueRef.current;
                apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, val !== "" ? val : null);
            }
        };
    }, []);

    const schema = object({
        value: number()
    })

    const validate = (value) => {
        if (field.currentValue.value == null || field.currentValue.value == undefined) {
            field.error = "Original Value undefined."
        }
        else if (schema.isValidSync({value: value})) {
            if (field.positive_only == true && value < 0) {
                field.error = "Should be positive."
            }
            else if (value == '') {
                field.error = "Should be a Float."
            }
            else {
                field.error = null;
            }
        }
        else {
            field.error = "Should be a Float."
        }

        setFieldValue(value);
        if (field.error != null) {
            return false
        } else {
            return true
        }

    }

    const updateValue = (value) => {
        triggerSave();
        apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, value !== "" ? value : null).then(
            async(response) => {
                if (response) {
                    const updatedField = await response.json()
                    item.sensitivityModelData.fields[field.name] = updatedField
                    resolveSave();
                    invalidateQueries();
                }
            }
        )
    }

    const handleSliderChange = (value: number) => {
        setSliderValue(value);
    }

    const handleCommittedChange = (value: number) => {
        const newValue = Math.round(originalValue * value) / 100;
        if (validate(newValue)) {
            fieldValueRef.current = newValue;
            hasChangedRef.current = false;
            updateValue(newValue);
        }
    }

    const handleComponentBlur = (e: React.FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node) && hasChangedRef.current) {
            updateValue(fieldValueRef.current);
            hasChangedRef.current = false;
        }
    }

    const valueLabelFormat = (value) => {
        if (originalValue == 0) {
            return '+ 0 %'
        }

        const percentDiff = value - 100;

        if (percentDiff < 0) {
            return '- ' + (Math.abs(percentDiff)).toString() + ' %'
        }
        else {
            return '+ ' + (percentDiff).toString() + ' %'
        }
    }

    const resetSensitivityValue = () => {
        apiService.deleteSensitivityValue(sensitivityField.id).then(
            async(response) => {
                if (response) {
                    delete item.sensitivityModelData.fields[field.name];
                    setSliderValue(100);
                    setFieldValue(originalValue)
                    invalidateQueries();
                }
            }
        )
        
    }

    return (
        <div className="flex flex-row items-center mt-1" onBlur={handleComponentBlur}>
            {!isReadOnly && (
                <button
                    onClick={resetSensitivityValue}
                    disabled={!sensitivityField}
                    className="mr-2 h-10 w-10 flex items-center justify-center text-slate-600 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <span className="material-symbols-outlined text-[30px]">restart_alt</span>
                </button>
            )}
            <div className="w-[300px] mr-3 mt-1">
                <TailwindSlider
                    value={sliderValue}
                    onChange={handleSliderChange}
                    onChangeCommitted={handleCommittedChange}
                    disabled={isReadOnly}
                    valueLabelDisplay='on'
                    valueLabelFormat={valueLabelFormat}
                    max={200}
                />
            </div>
            <div className="relative w-[200px]">
                <input
                    type="number"
                    step="any"
                    value={fieldValue}
                    placeholder={name}
                    onChange={(e) => {
                        if (validate(e.target.value)) {
                            const parsed = parseFloat(e.target.value);
                            setFieldValue(parsed);
                            setSliderValue(originalValue !== 0 ? Math.round(parsed / originalValue * 10000) / 100 : 100);
                            fieldValueRef.current = parsed;
                            hasChangedRef.current = true;
                        } else {
                            setSliderValue(100);
                        }
                    }}
                    readOnly={isReadOnly}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-full h-[40px] px-3 pr-12 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary read-only:opacity-50 read-only:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                {field.suffix && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                        {field.suffix}
                    </span>
                )}
                {field.error && (
                    <p className="text-xs text-red-600 mt-1">{field.error}</p>
                )}
            </div>
            <SaveIndicator status={saveStatus} className="ml-2" />
        </div>
    )
}
